import type { PropsWithChildren } from "react";
import { useContext } from "react";
import {
  Box,
  Button,
  Card,
  createStyles,
  Grid,
  Link as MuiLink,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { useLocation, useParams, useRouteMatch } from "react-router";
import { Link } from "react-router-dom";

import { Image, RoyaltyPayorImage } from "components";
import { BaseContainer } from "containers";
import AddRoyaltyAccountsChecklist, {
  useAddedAccountsChecklistInfo,
} from "containers/AddedRoyaltyAccountsChecklist";
import { AppContext } from "contexts";
import * as routes from "routes";
import { getPageURL } from "routes/config";
import { useRoyaltyPayors } from "services/royaltyPayor";
import theme from "theme";

import * as addRoyaltyAccountRoutes from "../routes";

const useStyles = makeStyles(() =>
  createStyles({
    exitButton: {
      marginTop: theme.spacing(5),
      paddingLeft: 0,
      textTransform: "capitalize",
      "&:hover": {
        color: theme.custom.palette.black.main,
      },
    },
    lastAddedSourceImageContainer: {
      width: "170px",
    },
    wrapText: {
      maxWidth: "170px",
      wordWrap: "break-word",
    },
    fontWeightRegular: {
      fontWeight: theme.typography.fontWeightRegular,
    },
    fontWeightMedium: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    logo: {
      height: "40px",
    },
  }),
);

const AddRoyaltyAccountContainer = ({ children }: PropsWithChildren) => {
  const classes = useStyles();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { domainNames } = useContext(AppContext);

  const {
    isAnyAccountConnected,
    lastAddedSource,
  } = useAddedAccountsChecklistInfo();
  const { data: royaltyPayors } = useRoyaltyPayors();
  const royaltyPayor = royaltyPayors?.find(
    royaltyPayor => royaltyPayor.name === lastAddedSource.royaltyPayorName,
  );

  const { royaltyPayorName, assetId } = useParams<{
    royaltyPayorName: string;
    assetId: string;
  }>();
  const isChecklistVisible = !useRouteMatch([
    addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccountSuccess(
      royaltyPayorName,
    ),
    addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccountSuccess(
      royaltyPayorName,
    ),
    addRoyaltyAccountRoutes.otherSourceSubmissionSuccess(),
  ]);

  const backRouteMap = addRoyaltyAccountRoutes.makeBackRouteMap(
    royaltyPayorName,
    assetId,
  );
  const backExitButton = addRoyaltyAccountRoutes.getBackRoute(
    useLocation().pathname,
    backRouteMap,
  );
  const isFirstStep = useRouteMatch(routes.sellerAddRoyaltyAccount()).isExact;
  const shouldExitToExchange = !isAnyAccountConnected && isFirstStep;

  return (
    <BaseContainer>
      <Box pb={8}>
        <Box
          mt={5}
          mb={5}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
        >
          <MuiLink href={getPageURL(routes.overview(), domainNames)}>
            <Image name="re-logo.svg" className={classes.logo} />
          </MuiLink>
          {shouldExitToExchange ? (
            <MuiLink href={getPageURL(routes.overview(), domainNames)}>
              <Button
                className={classes.exitButton}
                variant="text"
                startIcon={<ArrowBackIcon />}
                size="large"
              >
                Back to The Exchange
              </Button>
            </MuiLink>
          ) : (
            <Button
              className={classes.exitButton}
              variant="text"
              startIcon={<ArrowBackIcon />}
              size="large"
              component={Link}
              to={backExitButton.route}
            >
              {backExitButton.label}
            </Button>
          )}
        </Box>
        <Grid container justify="center" spacing={2}>
          {isChecklistVisible && !isSmDown && (
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box maxWidth="390px">
                  {isAnyAccountConnected ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      <Box mb={3}>
                        <Typography
                          variant="h5"
                          className={classes.fontWeightMedium}
                        >
                          Add more royalty accounts to increase the value of
                          your deal options.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Typography
                        variant="h5"
                        className={classes.fontWeightMedium}
                        gutterBottom
                      >
                        Start adding your royalty accounts to increase the value
                        of your deal options.
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        Royalty accounts collect the royalties for your catalog.
                        We can only provide deal options on connected royalty
                        accounts.
                      </Typography>
                    </>
                  )}
                  <AddRoyaltyAccountsChecklist />
                </Box>
                {isAnyAccountConnected && (
                  <Box width="390px">
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      mt={2}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        className={classes.fontWeightRegular}
                      >
                        Last Added Royalty Account
                      </Typography>
                      <Box width="fit-content">
                        <Card className={classes.lastAddedSourceImageContainer}>
                          <RoyaltyPayorImage
                            royaltyPayor={royaltyPayor}
                            royaltyPayorLogoMaxHeight="50px"
                          />
                        </Card>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          mt={1}
                        >
                          <Typography className={classes.wrapText}>
                            {!!royaltyPayor
                              ? lastAddedSource.royaltyAccountUsername
                              : lastAddedSource.royaltyPayorName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            {children}
          </Grid>
        </Grid>
      </Box>
    </BaseContainer>
  );
};

export default AddRoyaltyAccountContainer;
